<script setup lang="ts">
import { RouterLink } from 'hybridly/vue'

const props = defineProps<{
	scrapeItems: Paginator<App.Data.ScrapeItem.ScrapeItemData>
	filters: App.Data.ScrapeItem.SearchScrapeItemData
	competitors: App.Data.ScrapeItem.ScrapeItemCompetitorOptionsData[]
	sortOptions: App.Data.ScrapeItem.ScrapeItemSortOptionsData[]
}>()

const { user } = useUser()

const filterForm = ref(props.filters)
watchDebounced(
	filterForm.value,
	() => {
		router.get(route('products.index'), {
			data: filterForm.value,
			preserveState: true,
		})
	},
	{ debounce: 200, maxWait: 5000 },
)

const { copy, isSupported } = useClipboard({ legacy: true })
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/default.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
	<content-wrapper>
		<div class="mb-3 flex flex-wrap justify-between gap-3 border-b pb-3">
			<div class="flex-1">
				<div class="flex grow flex-wrap gap-2">
					<ui-input v-model="filterForm.keyword as string" class="min-w-40 max-w-60" :placeholder="t('app.search_placeholder')" />
					<ui-select v-model="filterForm.competitor as string">
						<ui-select-trigger class="min-w-40 max-w-60">
							<ui-select-value :placeholder="t('forms.scrape_items.select_competitor')" />
						</ui-select-trigger>
						<ui-select-content>
							<ui-select-item v-for="competitor in competitors" :key="competitor.key" :value="competitor.key">
								{{ competitor.label }}
							</ui-select-item>
						</ui-select-content>
					</ui-select>
					<ui-select v-model="filterForm.cheapest as string">
						<ui-select-trigger class="min-w-40 max-w-60">
							<ui-select-value :placeholder="t('forms.scrape_items.select_cheapest')" />
						</ui-select-trigger>
						<ui-select-content>
							<ui-select-item value="1">
								<div class="flex items-center gap-2">
									<div class="bg-successful size-2 rounded-full" />
									{{ user.current_team?.name }}
								</div>
							</ui-select-item>
							<ui-select-item value="0">
								<div class="flex items-center gap-2">
									<div class="bg-destructive size-2 rounded-full" />
									{{ t('app.competitor') }}
								</div>
							</ui-select-item>
						</ui-select-content>
					</ui-select>
					<ui-select v-model="filterForm.sort as string">
						<ui-select-trigger class="min-w-40 max-w-60">
							<ui-select-value :placeholder="t('forms.scrape_items.select_sort_option')" />
						</ui-select-trigger>
						<ui-select-content>
							<ui-select-item v-for="option in props.sortOptions" :key="option.key" :value="option.key">
								{{ option.label }}
							</ui-select-item>
						</ui-select-content>
					</ui-select>
					<ui-button :as="RouterLink" :href="route('products.index')" variant="link">
						{{ t('app.buttons.reset') }}
					</ui-button>
				</div>
			</div>

			<ui-button :as="RouterLink" :href="route('products.create')" variant="default">
				{{ t('app.buttons.add_new') }}
				<i-ph-plus-bold />
			</ui-button>
		</div>

		<div>
			<template v-if="props.scrapeItems.data.length === 0 && !props.filters">
				<p class="text-muted-foreground text-center text-sm">
					{{ t('views.scrape-items.index.empty_state') }}
				</p>
			</template>
			<template v-else>
				<template v-if="props.scrapeItems.data.length > 0">
					<div class="flex flex-col gap-3 divide-y">
						<div v-for="scrapeItem in props.scrapeItems.data" :key="scrapeItem.uuid" class="flex flex-col gap-3 pb-3 lg:flex-row">
							<div class="lg:w-lg">
								<div v-if="scrapeItem.last_processed_at" class="flex shrink-0 gap-4">
									<div class="bg-sidebar size-26 shrink-0 rounded-md p-1">
										<img
											:src="scrapeItem.image ?? ''"
											:alt="scrapeItem.name ?? ''"
											class="aspect-square object-contain object-center mix-blend-multiply"
											loading="lazy"
											onerror="this.style.display='none'"
										/>
									</div>
									<div class="flex flex-col">
										<strong class="text-sm leading-none">
											{{ scrapeItem.name }}
											<template v-if="isSupported">
												<ui-tooltip>
													<ui-tooltip-trigger>
														<i-ph-link-bold class="text-primary -mb-1 size-4 cursor-pointer" @click="copy(scrapeItem.full_url)" />
													</ui-tooltip-trigger>
													<ui-tooltip-content>
														{{ t('app.buttons.copy_url') }}
													</ui-tooltip-content>
												</ui-tooltip>
											</template>
										</strong>
										<div class="mt-0.5 flex items-center gap-1">
											{{ scrapeItem.latest_result?.price }}
											<div v-if="scrapeItem.is_cheapest" class="bg-successful size-2 rounded-full" />
											<div v-else-if="scrapeItem.latest_result?.price" class="bg-destructive size-2 rounded-full" />
										</div>
										<availability v-if="scrapeItem.latest_result?.availability" :availability="scrapeItem.latest_result?.availability" />
										<div class="text-muted-foreground flex items-center gap-1 text-xs">
											<i-ph-arrows-clockwise-bold />
											{{ scrapeItem.last_processed_at }}
											<template v-if="scrapeItem.error_description">
												<ui-tooltip>
													<ui-tooltip-trigger>
														<i-ph-warning-bold class="text-destructive size-4 cursor-pointer" />
													</ui-tooltip-trigger>
													<ui-tooltip-content class="bg-destructive">
														{{ scrapeItem.error_description }}
													</ui-tooltip-content>
												</ui-tooltip>
											</template>
										</div>
									</div>
								</div>
								<div v-else class="flex shrink-0 gap-4">
									<div class="bg-sidebar size-26 flex shrink-0 items-center justify-center rounded-md p-1">
										<i-ph-spinner-gap-bold class="text-primary size-6 animate-spin" />
									</div>
									<div class="flex flex-col gap-1">
										<strong class="text-sm leading-none">
											{{ scrapeItem.name }}
										</strong>
										<span class="text-muted-foreground text-sm">
											{{ t('app.scrape_item_not_processed') }}
										</span>
									</div>
								</div>
							</div>

							<div class="flex flex-wrap gap-3">
								<div v-for="childScrapeItem in scrapeItem.children" :key="childScrapeItem.uuid" class="bg-sidebar rounded-md p-2 lg:w-60">
									<div class="mb-1 flex items-center gap-2 border-b pb-1 text-sm">
										<ui-avatar-initials :name="childScrapeItem.competitor?.name ?? ''" class="size-8 rounded-md" :style="{ '--avatar-background': childScrapeItem.competitor?.brand_color_foreground, '--avatar-foreground': childScrapeItem.competitor?.brand_color }" />
										<strong>{{ childScrapeItem.competitor?.name }}</strong>
										<template v-if="isSupported">
											<ui-tooltip>
												<ui-tooltip-trigger>
													<i-ph-link-bold class="text-primary size-4 cursor-pointer" @click="copy(childScrapeItem.full_url)" />
												</ui-tooltip-trigger>
												<ui-tooltip-content>
													{{ t('app.buttons.copy_url') }}
												</ui-tooltip-content>
											</ui-tooltip>
										</template>
									</div>

									<div v-if="childScrapeItem.last_processed_at" class="flex flex-col gap-1">
										<div class="flex flex-col">
											<div class="flex flex-wrap justify-between gap-2">
												<div class="flex items-center gap-1">
													{{ childScrapeItem.latest_result?.price }}
													<div v-if="childScrapeItem.is_cheapest" class="bg-successful size-2 rounded-full" />
												</div>
												<availability v-if="childScrapeItem.latest_result?.availability" :availability="childScrapeItem.latest_result?.availability" />
											</div>
										</div>

										<div class="text-muted-foreground flex items-center gap-1 text-xs">
											<i-ph-arrows-clockwise-bold />
											{{ childScrapeItem.last_processed_at }}
											<template v-if="childScrapeItem.error_description">
												<ui-tooltip>
													<ui-tooltip-trigger>
														<i-ph-warning-bold class="text-destructive size-4 cursor-pointer" />
													</ui-tooltip-trigger>
													<ui-tooltip-content class="bg-destructive">
														{{ childScrapeItem.error_description }}
													</ui-tooltip-content>
												</ui-tooltip>
											</template>
										</div>
									</div>
									<div v-else class="flex items-center gap-2">
										<i-ph-spinner-gap-bold class="text-primary size-6 animate-spin" />
										<span class="text-muted-foreground text-xs">
											{{ t('app.scrape_item_not_processed') }}
										</span>
									</div>
								</div>
							</div>

							<div class="flex gap-3 lg:ml-auto">
								<ui-button :as="RouterLink" :href="route('products.show', { scrapeItem: scrapeItem.uuid })" variant="link">
									{{ t('app.buttons.details') }}
								</ui-button>

								<ui-button :as="RouterLink" :href="route('products.edit', { scrapeItem: scrapeItem.uuid })" variant="link">
									{{ t('app.buttons.edit') }}
								</ui-button>
							</div>
						</div>
					</div>
				</template>
				<p v-else class="text-muted-foreground text-center text-sm">
					{{ t('views.scrape-items.index.no_results') }}
				</p>

				<pagination :links="props.scrapeItems.links" />
			</template>
		</div>
	</content-wrapper>
</template>
